/**
 * DecisionFlow — Magic Link Landing
 * ui/src/screens/MagicLinkLanding.jsx
 * data-fit LLC · S664 · July 31, 2026
 *
 * Public route: /magic-link?token=<raw_token>
 * No auth required. Rep persona passwordless login.
 * Calls POST /api/v1/auth/magic-link/verify.
 * On success → loginWithSession() → navigate to /rep/
 * Token TTL: 15 minutes. Single use.
 *
 * spec_auth_identity_s661.md §4 — magic link login
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";

const API_BASE = "/api/v1";

function errorFromCode(code, message) {
  switch (code) {
    case "TOKEN_INVALID":
      return "This login link is invalid or has expired. Magic links expire after 15 minutes and can only be used once.";
    case "USER_ACCOUNT_INACTIVE":
      return "Your account is inactive. Contact your administrator.";
    case "USER_ACCOUNT_LOCKED":
      return "Your account is locked. Contact your administrator.";
    default:
      return message || "Something went wrong. Please request a new login link.";
  }
}

export default function MagicLinkLanding() {
  const [status, setStatus] = useState("verifying"); // verifying | success | error | missing
  const [error,  setError]  = useState(null);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { loginWithSession } = useAuth();

  const token = new URLSearchParams(location.search).get("token") ?? "";

  useEffect(() => {
    if (!token) {
      setStatus("missing");
      return;
    }

    let cancelled = false;

    async function verify() {
      try {
        const res = await fetch(`${API_BASE}/auth/magic-link/verify`, {
          method:      "POST",
          headers:     { "Content-Type": "application/json" },
          credentials: "include", // cookie set by server on success
          body:        JSON.stringify({ token }),
        });

        if (cancelled) return;

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const code = data?.error?.code ?? "";
          const msg  = data?.error?.message ?? "";
          setError(errorFromCode(code, msg));
          setStatus("error");
          return;
        }

        // Session cookie set by server. Fetch user profile then
        // hydrate AuthContext via loginWithSession.
        const meRes = await fetch(`${API_BASE}/auth/me`, {
          credentials: "include",
        });

        if (cancelled) return;

        if (!meRes.ok) {
          setError("Login succeeded but could not load your profile. Please try again.");
          setStatus("error");
          return;
        }

        const me = await meRes.json();
        if (cancelled) return;

        await loginWithSession(me);

        setStatus("success");
        // Rep persona always lands at /rep/
        navigate("/rep/", { replace: true });
      } catch (_) {
        if (!cancelled) {
          setError("Unable to connect. Check your connection and try again.");
          setStatus("error");
        }
      }
    }

    verify();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scoped CSS ──────────────────────────────────────────────────────────────
  const css = `
    .dfml-root {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: rgb(var(--color-bg));
      font-family: "IBM Plex Sans", "Segoe UI", Arial, sans-serif;
      padding: 24px;
    }

    .dfml-card {
      width: 100%;
      max-width: 400px;
      background: rgb(var(--color-surface));
      border: 1px solid rgb(var(--color-border));
      border-radius: 14px;
      padding: 48px 40px 40px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.06);
      text-align: center;
    }

    .dfml-identity { margin-bottom: 36px; }
    .dfml-identity-brand {
      font-size: var(--font-sz-xs);
      font-weight: 500;
      color: rgb(var(--color-text-muted));
      letter-spacing: 0.04em;
      margin-bottom: 3px;
    }
    .dfml-identity-name {
      font-size: var(--font-sz-xl);
      font-weight: 700;
      color: rgb(var(--color-primary));
      letter-spacing: -0.01em;
      line-height: 1.1;
    }

    .dfml-icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      font-size: 26px;
    }
    .dfml-icon-verifying {
      background: rgba(var(--color-primary-rgb, 59,130,246), 0.08);
      animation: dfml-pulse 1.4s ease-in-out infinite;
    }
    .dfml-icon-error {
      background: rgba(var(--color-danger-rgb, 220,38,38), 0.08);
    }
    .dfml-icon-missing {
      background: rgba(var(--color-warning-rgb, 245,158,11), 0.08);
    }

    @keyframes dfml-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.4; }
    }

    .dfml-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      margin-bottom: 10px;
    }
    .dfml-body {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      line-height: 1.65;
      margin-bottom: 28px;
    }

    .dfml-btn {
      width: 100%;
      padding: 12px;
      background: rgb(var(--color-primary));
      color: #ffffff;
      border: none;
      border-radius: 7px;
      font-size: var(--font-sz-base);
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms;
      letter-spacing: 0.01em;
      margin-bottom: 12px;
    }
    .dfml-btn:hover { background: rgb(var(--color-primary-hover)); }

    .dfml-link-btn {
      background: none;
      border: none;
      color: rgb(var(--color-text-muted));
      font-size: var(--font-sz-sm);
      font-family: inherit;
      cursor: pointer;
      padding: 0;
      text-decoration: none;
    }
    .dfml-link-btn:hover { color: rgb(var(--color-text)); text-decoration: underline; }

    .dfml-spinner {
      display: inline-block;
      width: 18px;
      height: 18px;
      border: 2px solid rgb(var(--color-border));
      border-top-color: rgb(var(--color-primary));
      border-radius: 50%;
      animation: dfml-spin 0.7s linear infinite;
      margin-bottom: 20px;
    }
    @keyframes dfml-spin {
      to { transform: rotate(360deg); }
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="dfml-root">
        <div className="dfml-card">

          {/* Product identity */}
          <div className="dfml-identity">
            <div className="dfml-identity-brand">data-fit LLC</div>
            <div className="dfml-identity-name">DecisionFlow</div>
          </div>

          {status === "verifying" && (
            <>
              <div className="dfml-spinner" aria-hidden="true" />
              <div className="dfml-title">Signing you in…</div>
              <div className="dfml-body">
                Verifying your login link. This takes just a moment.
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="dfml-icon dfml-icon-error" aria-hidden="true">✕</div>
              <div className="dfml-title">Login link expired</div>
              <div className="dfml-body">{error}</div>
              <button
                className="dfml-btn"
                onClick={() => navigate("/login")}
              >
                Request a new login link
              </button>
            </>
          )}

          {status === "missing" && (
            <>
              <div className="dfml-icon dfml-icon-missing" aria-hidden="true">⚠</div>
              <div className="dfml-title">Invalid link</div>
              <div className="dfml-body">
                This link is missing a login token. Use the link from your
                login email, or return to the sign-in page to request a new one.
              </div>
              <button
                className="dfml-btn"
                onClick={() => navigate("/login")}
              >
                Go to sign in
              </button>
            </>
          )}

        </div>
      </div>
    </>
  );
}
